import React, { useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { firebaseService } from '../../services/firebaseService';
import OnboardingFlow from '../onboarding/OnboardingFlow';
import ActionLoader from '../widgets/ActionLoader';
import { Box, Typography } from '@mui/material';
import { authService } from '../../services/authService';
import { isFirebaseInitialized } from '@watchlist/shared';
import { font } from '../../utils/fontHelper';

interface OnboardingWrapperProps {
  user: User;
  children: React.ReactNode;
  onOnboardingComplete?: () => void;
}

const OnboardingWrapper: React.FC<OnboardingWrapperProps> = ({
  user,
  children,
  onOnboardingComplete,
}) => {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      try {
        // Check if Firebase is properly initialized before attempting operations
        if (!isFirebaseInitialized()) {
          console.warn('OnboardingWrapper: Firebase not yet initialized, skipping onboarding status check');
          setLoading(false);
          setShowOnboarding(false); // Default to not showing onboarding until Firebase is ready
          return;
        }

        console.log('🔍 OnboardingWrapper: Checking onboarding status for user:', user.uid);
        const preferences = await authService.getUserPreferences(user);
        
        if (!preferences) {
          console.log('📝 OnboardingWrapper: No preferences found - new user needs onboarding');
          setShowOnboarding(true);
        } else {
          // Show onboarding if onboardingCompleted is explicitly false AND user hasn't skipped
          const isCompleted = preferences.onboardingCompleted === true;
          const isSkipped = preferences.onboardingSkipped === true;
          const needsOnboarding = !isCompleted && !isSkipped;
          
          console.log('📊 OnboardingWrapper: Onboarding status check:', {
            onboardingCompleted: preferences.onboardingCompleted,
            onboardingSkipped: preferences.onboardingSkipped,
            needsOnboarding,
            hasResetTimestamp: !!preferences.onboardingResetAt
          });
          
          setShowOnboarding(needsOnboarding);
        }
      } catch (error) {
        console.error('❌ OnboardingWrapper: Error checking onboarding status:', error);
        // Default to showing onboarding if we can't determine status (better safe than sorry)
        console.log('🛡️ OnboardingWrapper: Defaulting to show onboarding due to error');
        setShowOnboarding(true);
      } finally {
        setLoading(false);
      }
    };

    checkOnboardingStatus();
  }, [user]);

  const handleOnboardingComplete = async () => {
    try {
      console.log('🎉 OnboardingWrapper: Onboarding completed for user:', user.uid);
      
      // First, hide onboarding to show the homepage
      setShowOnboarding(false);
      
      // Wait a moment for the homepage to render, then show welcome message
      setTimeout(() => {
        console.log('🎊 OnboardingWrapper: Starting welcome message');
        setShowWelcome(true);
        
        // Auto-hide welcome message after 2.5 seconds
        setTimeout(() => {
          handleWelcomeComplete();
        }, 2500);
      }, 500); // 500ms delay to ensure homepage loads first
      
    } catch (error) {
      console.error('❌ OnboardingWrapper: Error completing onboarding:', error);
      // If there's an error, still proceed to completion
      setShowOnboarding(false);
      if (onOnboardingComplete) {
        onOnboardingComplete();
      }
    }
  };

  const handleWelcomeComplete = () => {
    setShowWelcome(false);
    if (onOnboardingComplete) {
      onOnboardingComplete();
    }
  };

  const handleSkipOnboarding = async () => {
    try {
      // Check if Firebase is properly initialized before attempting operations
      if (!isFirebaseInitialized()) {
        console.warn('OnboardingWrapper: Firebase not yet initialized, cannot skip onboarding');
        return;
      }

      console.log('⏭️ OnboardingWrapper: User skipped onboarding:', user.uid);
      // Mark onboarding as completed without collecting data
      await authService.updateUserPreferences(user.uid, {
        onboardingCompleted: true,
        preferredLanguages: ['en'], // Default language
        language: 'en',
      });

      setShowOnboarding(false);
      
      if (onOnboardingComplete) {
        onOnboardingComplete();
      }
    } catch (error) {
      console.error('❌ OnboardingWrapper: Error skipping onboarding:', error);
    }
  };

  if (loading) {
    return <ActionLoader />;
  }

  if (showOnboarding) {
    return (
      <OnboardingFlow
        open={true}
        user={user}
        onClose={handleSkipOnboarding}
        onComplete={handleOnboardingComplete}
      />
    );
  }

  if (showWelcome) {
    return (
      <>
        {children}
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(10px)',
            zIndex: 999999,
          }}
        >
          <ActionLoader size={80} />
          <Typography
            sx={{
              mt: 4,
              fontFamily: font(),
              fontWeight: 500,
              fontSize: '18px',
              color: 'rgba(255, 255, 255, 0.9)',
              textAlign: 'center',
            }}
          >
            Welcome to Watchlists
          </Typography>
        </Box>
      </>
    );
  }

  return <>{children}</>;
};

export default OnboardingWrapper; 